import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { DEFAULT_HOME } from "../lib/homeContent";

const EASE = [0.16, 1, 0.3, 1];

const SERVICE_SLUGS = ["irecs", "blockchain-audits", "government-grants", "patient-capital", "waste2energy", "sblc"];
const SERVICE_SPANS = ["lg:col-span-8", "lg:col-span-4", "lg:col-span-4", "lg:col-span-8", "lg:col-span-7", "lg:col-span-5"];
const PHOTO_CARDS = new Set([1, 4, 5]);

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const ServiceBody = ({ s }) => (
  <div className="relative z-10 flex h-full min-h-[24rem] flex-col justify-end p-8 md:p-10">
    <span className="absolute right-6 top-6 font-mono text-xs tracking-widest text-white/35">{s.num}</span>
    <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-amber-300/80">{s.kicker}</p>
    <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-snug text-white">{s.title}</h3>
    <p className="mt-3 max-w-md font-sans text-sm leading-relaxed text-white/65">{s.body}</p>
    {s.subs.length > 0 && (
      <ul className="mt-6 space-y-2 border-t border-white/10 pt-5">
        {s.subs.map((sub) => (
          <li key={sub} className="flex items-center gap-3 font-sans text-sm text-white/60">
            <span className="text-[0.5rem] text-amber-400/80">&#9670;</span>
            {sub}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function Home() {
  const [c, setC] = useState(DEFAULT_HOME);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api
      .get("/content/home")
      .then(({ data }) => setC({ ...DEFAULT_HOME, ...data }))
      .catch(() => {});
  }, []);

  const services = [1, 2, 3, 4, 5, 6].map((i) => ({
    num: `0${i}`,
    slug: SERVICE_SLUGS[i - 1],
    span: SERVICE_SPANS[i - 1],
    photo: PHOTO_CARDS.has(i),
    kicker: c[`service_${i}_kicker`],
    title: c[`service_${i}_title`],
    body: c[`service_${i}_body`],
    subs: [c[`service_${i}_sub_1`], c[`service_${i}_sub_2`], c[`service_${i}_sub_3`]].filter(Boolean),
    img: c[`service_${i}_image`],
  }));

  const principles = [1, 2, 3, 4].map((i) => ({
    title: c[`principle_${i}_title`],
    label: c[`principle_${i}_label`],
  }));

  const marqueeTitles = services.map((s) => s.title).filter(Boolean);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-center overflow-hidden bg-sp-dark sp-grain">
        {c.hero_image && (
          <motion.img
            src={c.hero_image}
            alt=""
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: EASE }}
            style={{ y: heroImgY }}
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-sp-dark via-sp-dark/80 to-sp-dark/30" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 w-full pt-24">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="sp-eyebrow text-sky-300/80"
          >
            {c.hero_eyebrow}
          </motion.p>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-white max-w-4xl">
            <MaskedLine delay={0.15}>{c.hero_title}</MaskedLine>
            {c.hero_title_accent && (
              <MaskedLine delay={0.28}>
                <span className="italic text-sky-200">{c.hero_title_accent}</span>
              </MaskedLine>
            )}
            <MaskedLine delay={0.41}>{c.hero_title_end}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
            className="mt-8 max-w-2xl font-sans text-lg leading-relaxed text-white/70"
          >
            {c.hero_subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/connect"
              data-testid="hero-connect-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-medium text-sp-deep hover:bg-sky-100 transition-colors duration-300"
            >
              {c.hero_cta_primary}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
            <Link
              to="/who-we-are"
              data-testid="hero-about-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-medium text-white hover:bg-white/5 transition-colors duration-300"
            >
              {c.hero_cta_secondary}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SERVICE MARQUEE */}
      <section className="bg-sp-dark border-y border-white/10 overflow-hidden" aria-hidden="true">
        <div className="sp-marquee flex w-max items-center py-5">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center">
              {marqueeTitles.map((t) => (
                <span key={`${dup}-${t}`} className="flex items-center">
                  <span className="font-serif text-lg md:text-xl italic text-white/60">{t}</span>
                  <span className="mx-8 text-[0.55rem] text-amber-400/70">&#9670;</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / STATEMENT */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-28 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <div className="md:col-span-4">
            <p className="sp-eyebrow">{c.intro_eyebrow}</p>
          </div>
          <motion.div
            className="md:col-span-8"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-sp-deep">
              {c.intro_heading}
            </h2>
            <p className="mt-8 font-sans text-lg leading-relaxed text-sp-soft max-w-2xl">
              {c.intro_body}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-section" className="bg-white border-y border-sp-navy/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <p className="sp-eyebrow">{c.services_eyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl text-sp-deep">{c.services_heading}</h2>
            </div>
            <Link to="/who-we-are" data-testid="services-about-link" className="sp-link-underline font-sans text-sm text-sp-navy">
              About the firm &rarr;
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8">
            {services.map((s, i) => (
              <motion.article
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE, delay: (i % 2) * 0.1 }}
                className={`group relative overflow-hidden rounded-sm border border-white/10 bg-sp-dark ${s.span} ${
                  s.photo && s.img ? "" : "sp-grain"
                } transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-amber-400/40 hover:shadow-2xl hover:shadow-sp-dark/40`}
              >
                {s.photo && s.img && (
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="h-full w-full object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sp-dark via-sp-dark/75 to-sp-dark/25" />
                  </div>
                )}
                <ServiceBody s={s} />
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-sp-dark sp-grain relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
          <p className="sp-eyebrow text-sky-300/70">How We Work</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-10">
            {principles.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                data-testid={`principle-${i}`}
              >
                <p className="font-serif text-3xl md:text-4xl text-white">{s.title}</p>
                <p className="mt-3 font-sans text-sm leading-relaxed text-white/50">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-28 text-center">
        <p className="sp-eyebrow">{c.cta_eyebrow}</p>
        <h2 className="mt-6 font-serif text-3xl md:text-5xl text-sp-deep max-w-3xl mx-auto leading-[1.15]">
          {c.cta_heading}
        </h2>
        <p className="mt-6 font-sans text-lg text-sp-soft max-w-xl mx-auto">
          {c.cta_body}
        </p>
        <Link
          to="/connect"
          data-testid="cta-connect-btn"
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-sp-navy px-9 py-4 text-sm font-medium text-white hover:bg-sp-deep transition-colors duration-300"
        >
          {c.cta_button}
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
        </Link>
      </section>
    </div>
  );
}
